import React from "react";
import { Alert, Box, Typography } from "@mui/material";

export type DattoAccessCheck = {
  id: string;
  label: string;
  status: string;
  detail?: string;
};

type DattoAccessTestResult = {
  success?: boolean;
  has_warnings?: boolean;
  message?: string;
  checks?: DattoAccessCheck[];
};

const textStyle = { fontFamily: "var(--theo-font-sans)", fontSize: 13, lineHeight: 1.4 };

function checkStatus(status: string) {
  switch (status.toLowerCase()) {
    case "success": return { label: "Success", color: "#15803d", severity: "success" };
    case "failure":
    case "failed":
    case "error": return { label: "Failure", color: "#b91c1c", severity: "error" };
    case "warning": return { label: "Warning", color: "#92400e", severity: "warning" };
    case "skipped": return { label: "Skipped", color: "#4b5563", severity: "warning" };
    default: return { label: "Not checked", color: "#4b5563", severity: "warning" };
  }
}

/** Datto's connection-test checklist, shared by configuration and onboarding. */
const DattoAccessFeedbackAlert: React.FC<{ result: DattoAccessTestResult }> = ({ result }) => {
  const checks = result.checks || [];
  const hasFailure = checks.some((check) => checkStatus(check.status).severity === "error");
  const hasWarning = result.has_warnings || checks.some((check) => checkStatus(check.status).severity === "warning");
  const severity = hasFailure || result.success === false ? "error" : hasWarning ? "warning" : "success";

  return (
    <Alert severity={severity} sx={{ mt: 2, py: 1.25, "& .MuiAlert-message": { width: "100%" } }}>
      <Typography sx={{ ...textStyle, fontWeight: 600, mb: 0.75 }}>
        Datto connection and access checks
      </Typography>
      {result.message ? <Typography sx={{ ...textStyle, mb: 0.75 }}>{result.message}</Typography> : null}
      {checks.length ? (
        <Box component="ul" aria-label="Datto access checks" sx={{ m: 0, pl: 2, "& li": { mb: 0.75 }, "& li:last-of-type": { mb: 0 } }}>
          {checks.map((check) => {
            const status = checkStatus(check.status);
            return (
              <Typography component="li" key={check.id} sx={textStyle}>
                <Typography component="span" sx={{ ...textStyle, fontWeight: 700, color: status.color }}>
                  {status.label}
                </Typography>{" "}
                <strong>{check.label}</strong>
                {check.detail ? (
                  <Typography component="span" sx={{ ...textStyle, display: "block", mt: 0.25, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                    {check.detail}
                  </Typography>
                ) : null}
              </Typography>
            );
          })}
        </Box>
      ) : null}
    </Alert>
  );
};

export default DattoAccessFeedbackAlert;
